import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AuthFlow,
  ChatList,
  ChatView,
  PublicMessagesPanel,
  LinkToOrderModal,
} from "./components";
import {
  useTelegramAuth,
  useTelegramChats,
  useTelegramMessages,
  useTelegramWebSocket,
  setOnSessionExpired,
  clearMediaCache,
} from "./hooks";
import { useStickers } from "./hooks/useStickers";
import { useGifs } from "./hooks/useGifs";
import type {
  TelegramChat,
  TelegramMessage,
  PublicMessage,
  SendStickerData,
  SendGifData,
  ChatUpdate,
  TypingEvent,
  UserStatusEvent,
  ReadHistoryEvent,
} from "./types";

export function TelegramClientPage() {
  const {
    isAuthorized,
    isLoading: isAuthLoading,
    session,
    accounts,
    switchAccount,
    removeAccount,
    refetchSession,
  } = useTelegramAuth();
  const [selectedChat, setSelectedChat] = useState<TelegramChat | null>(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [linkToOrderModalOpen, setLinkToOrderModalOpen] = useState(false);
  const [linkToOrderChatId, setLinkToOrderChatId] = useState<number | null>(null);

  const {
    chats,
    publicMessages,
    isLoading: isChatsLoading,
    isLoadingMore: isChatsLoadingMore,
    hasMore: hasMoreChats,
    isPublicLoading,
    fetchMoreChats,
    updateChat,
    linkChatToOrder,
    unlinkChatFromOrder,
    refetch: refetchChats,
    refetchPublic,
  } = useTelegramChats({});

  const {
    messages,
    isLoading: isMessagesLoading,
    isLoadingMore: isMessagesLoadingMore,
    hasMore: hasMoreMessages,
    isSending,
    isUploadingFile,
    sendMessage,
    editMessage,
    deleteMessage,
    forwardMessage,
    forwardMessages,
    markAsPublic,
    sendFile,
    isMessageRead,
    markAsRead,
    updateMessage,
    removeMessages,
    refetch: refetchMessages,
    fetchMoreMessages,
  } = useTelegramMessages(selectedChat?.id ?? null, {
    // Передаём readOutboxMaxId из выбранного чата для определения прочитанности
    readOutboxMaxId: selectedChat?.readOutboxMaxId,
  });

  // Авто-открытие диалога из ?tgId / ?username (переход с заказа/профиля).
  // Ждём загрузки chats, матчим по telegramChatId или username, чистим URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || chats.length === 0) {return;}
    const tgId = searchParams.get("tgId");
    const username = searchParams.get("username");
    if (!tgId && !username) {return;}

    const target = chats.find((c: TelegramChat) =>
      (tgId && String(c.telegramChatId) === tgId) ||
      (username && c.username?.toLowerCase() === username.toLowerCase())
    );
    autoOpenedRef.current = true;
    if (target) {setSelectedChat(target);}
    // Убираем параметры, чтобы перезагрузка/reselect не переоткрывали чат
    searchParams.delete("tgId");
    searchParams.delete("username");
    setSearchParams(searchParams, { replace: true });
  }, [chats, searchParams, setSearchParams]);

  // Typing indicator state
  const [typingUsers, setTypingUsers] = useState<Map<number, { userId: number; action: string; timeout: ReturnType<typeof setTimeout> }>>(new Map());

  // Хуки для стикеров и GIF
  const { sendSticker } = useStickers();
  const { sendGif } = useGifs();

  // Обработчики отправки стикеров и GIF
  const handleSendSticker = useCallback(async (data: SendStickerData) => {
    if (!selectedChat) {return;}
    const success = await sendSticker(selectedChat.id, data);
    if (success) {
      refetchMessages();
    }
  }, [selectedChat, sendSticker, refetchMessages]);

  const handleSendGif = useCallback(async (data: SendGifData) => {
    if (!selectedChat) {return;}
    const success = await sendGif(selectedChat.id, data);
    if (success) {
      refetchMessages();
    }
  }, [selectedChat, sendGif, refetchMessages]);

  // Устанавливаем колбэк для обработки истечения Telegram сессии
  // При ошибке авторизации в запросах медиа — перезапрашиваем статус сессии
  useEffect(() => {
    setOnSessionExpired(() => {
      // Очищаем кэш медиа при истечении сессии
      clearMediaCache();
      // Сбрасываем выбранный чат
      setSelectedChat(null);
      // Перезапрашиваем статус сессии — это покажет форму авторизации
      refetchSession();
    });

    // Очищаем колбэк при размонтировании
    return () => {
      setOnSessionExpired(null);
    };
  }, [refetchSession]);

  // Загружаем чаты напрямую из Telegram при авторизации и обновляем каждые 30 секунд
  useEffect(() => {
    if (!isAuthorized || isAuthLoading) {return;}

    refetchChats();
    refetchPublic();

    const refreshInterval = setInterval(refetchChats, 30000);
    return () => clearInterval(refreshInterval);
  }, [isAuthorized, isAuthLoading, refetchChats, refetchPublic]);

  // WebSocket callbacks - стабилизируем через useCallback
  const handleWsNewMessage = useCallback(
    (chatId: number, _message: TelegramMessage) => {
      // Если сообщение для текущего выбранного чата — обновляем список сообщений
      if (selectedChat?.id === chatId) {
        refetchMessages();
      }
    },
    [selectedChat?.id, refetchMessages]
  );

  const handleWsPublicMessage = useCallback((_message: PublicMessage) => {
    refetchPublic();
  }, [refetchPublic]);

  const handleWsSessionExpired = useCallback(() => {
    refetchSession();
  }, [refetchSession]);

  // Обработчик обновлений чата (readOutboxMaxId, unreadCount, lastMessage)
  const handleWsChatUpdate = useCallback((update: ChatUpdate) => {
    // Обновляем чат в списке
    updateChat(update.chatId, {
      lastMessage: update.lastMessage,
      lastMessageDate: update.lastMessageDate,
      unreadCount: update.unreadCount,
      readOutboxMaxId: update.readOutboxMaxId,
    });

    // Если это текущий выбранный чат — обновляем и его
    if (selectedChat?.id === update.chatId) {
      setSelectedChat(prev => {
        if (!prev) {return null;}
        return {
          ...prev,
          lastMessage: update.lastMessage ?? prev.lastMessage,
          lastMessageDate: update.lastMessageDate ?? prev.lastMessageDate,
          unreadCount: update.unreadCount ?? prev.unreadCount,
          readOutboxMaxId: update.readOutboxMaxId ?? prev.readOutboxMaxId,
        };
      });

      // Если пришло обновление readOutboxMaxId — перезапрашиваем сообщения
      // чтобы обновить статус "прочитано" у исходящих сообщений
      if (update.readOutboxMaxId !== undefined) {
        refetchMessages();
      }
    }
  }, [updateChat, selectedChat?.id, refetchMessages]);

  // Обработчик редактирования сообщения через WS
  const handleWsMessageEdited = useCallback(
    (chatId: number, message: TelegramMessage) => {
      if (selectedChat?.id === chatId) {
        updateMessage(message.id, message);
      }
    },
    [selectedChat?.id, updateMessage]
  );

  // Обработчик удаления сообщений через WS
  const handleWsMessagesDeleted = useCallback(
    (chatId: number, messageIds: number[]) => {
      // chatId = 0 означает что Telegram не сообщил chatId — удаляем из текущего чата по id сообщения
      if (chatId === 0 || selectedChat?.id === chatId) {
        removeMessages(messageIds);
      }
    },
    [selectedChat?.id, removeMessages]
  );

  // Обработчик typing через WS
  const handleWsTyping = useCallback(
    (event: TypingEvent) => {
      if (selectedChat?.id !== event.chatId) {return;}

      if (event.action === 'cancel') {
        setTypingUsers(prev => {
          const next = new Map(prev);
          const existing = next.get(event.userId);
          if (existing) {
            clearTimeout(existing.timeout);
          }
          next.delete(event.userId);
          return next;
        });
        return;
      }

      setTypingUsers(prev => {
        const next = new Map(prev);
        // Очищаем предыдущий таймаут для этого пользователя
        const existing = next.get(event.userId);
        if (existing) {
          clearTimeout(existing.timeout);
        }
        // Typing индикатор автоматически исчезает через 6 секунд
        const timeout = setTimeout(() => {
          setTypingUsers(p => {
            const n = new Map(p);
            n.delete(event.userId);
            return n;
          });
        }, 6000);
        next.set(event.userId, { userId: event.userId, action: event.action, timeout });
        return next;
      });
    },
    [selectedChat?.id]
  );

  // Обработчик статуса пользователя через WS (не используем пока, но пробрасываем)
  const handleWsUserStatus = useCallback((_event: UserStatusEvent) => {
    // TODO: можно обновлять статус в ChatList, если потребуется
  }, []);

  // Обработчик прочитанности через WS
  const handleWsReadHistory = useCallback(
    (event: ReadHistoryEvent) => {
      if (selectedChat?.id !== event.chatId) {return;}

      if (event.direction === 'outbox') {
        // Собеседник прочитал наши сообщения — обновляем readOutboxMaxId
        setSelectedChat(prev => {
          if (!prev) {return null;}
          return { ...prev, readOutboxMaxId: event.maxId };
        });
      }
    },
    [selectedChat?.id]
  );

  // WebSocket for real-time updates
  const { subscribeToChat, unsubscribeFromChat, connectionState } = useTelegramWebSocket({
    enabled: isAuthorized,
    onNewMessage: handleWsNewMessage,
    onMessageEdited: handleWsMessageEdited,
    onMessagesDeleted: handleWsMessagesDeleted,
    onPublicMessage: handleWsPublicMessage,
    onChatUpdate: handleWsChatUpdate,
    onTyping: handleWsTyping,
    onUserStatus: handleWsUserStatus,
    onReadHistory: handleWsReadHistory,
    onSessionExpired: handleWsSessionExpired,
  });

  // Подписка на выбранный чат для получения real-time обновлений
  useEffect(() => {
    if (selectedChat?.id) {
      subscribeToChat(selectedChat.id);
    }
    // Отписываемся при смене чата или размонтировании
    return () => {
      if (selectedChat?.id) {
        unsubscribeFromChat(selectedChat.id);
      }
    };
  }, [selectedChat?.id, subscribeToChat, unsubscribeFromChat]);

  // Помечаем сообщения как прочитанные при открытии чата
  // Используем ref для отслеживания последнего помеченного ID, чтобы не делать лишние запросы
  const lastMarkedReadIdRef = useRef<number>(0);
  useEffect(() => {
    if (selectedChat?.id && messages.length > 0) {
      // Находим максимальный ID входящих сообщений
      const incomingMessages = messages.filter(m => !m.isOutgoing);
      if (incomingMessages.length > 0) {
        const maxId = Math.max(...incomingMessages.map(m => m.id));
        // Помечаем только если есть новые непрочитанные
        if (maxId > lastMarkedReadIdRef.current) {
          lastMarkedReadIdRef.current = maxId;
          markAsRead(maxId);
          // Обнуляем unreadCount в списке чатов
          if (selectedChat.unreadCount > 0) {
            updateChat(selectedChat.id, { unreadCount: 0 });
            // Также обновляем локальный selectedChat
            setSelectedChat(prev => prev ? { ...prev, unreadCount: 0 } : null);
          }
        }
      }
    }
  }, [selectedChat?.id, selectedChat?.unreadCount, messages, markAsRead, updateChat]);

  // Сбрасываем lastMarkedReadId при смене чата
  useEffect(() => {
    lastMarkedReadIdRef.current = 0;
  }, [selectedChat?.id]);

  // Handle chat selection — abort предыдущего чата при переключении
  const handleSelectChat = useCallback((chat: TelegramChat) => {
    // Отменяем серверные запросы предыдущего чата
    if (selectedChat && selectedChat.id !== chat.id) {
      fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/telegram-client/chats/${selectedChat.id}/abort`, {
        method: 'POST',
      }).catch(() => {
        // Игнорируем ошибки abort — не критично
      });
    }
    setSelectedChat(chat);
    // When switching chats, messages will be cleared by useTelegramMessages
    // and the loading state will show the loader
  }, [selectedChat]);

  // Handle link to order
  const handleLinkToOrder = useCallback((chatId: number) => {
    setLinkToOrderChatId(chatId);
    setLinkToOrderModalOpen(true);
  }, []);

  // Handle close link to order modal
  const handleCloseLinkToOrderModal = useCallback(() => {
    setLinkToOrderModalOpen(false);
    setLinkToOrderChatId(null);
  }, []);

  // Handle open chat from public messages panel
  const handleOpenChatFromPublic = useCallback(
    (chatId: number) => {
      const chat = chats.find((c: TelegramChat) => c.id === chatId);
      if (chat) {
        setSelectedChat(chat);
      }
    },
    [chats]
  );

  // Handle open order
  const handleOpenOrder = useCallback((orderId: number) => {
    // Navigate to order page
    window.open(`/orders/${orderId}`, "_blank");
  }, []);

  // Handle switch account - clear current state and refetch
  const handleSwitchAccount = useCallback(async (sessionId: number) => {
    setSelectedChat(null);
    const success = await switchAccount(sessionId);
    if (success) {
      clearMediaCache();
      refetchChats();
      refetchPublic();
    }
    return success;
  }, [switchAccount, refetchChats, refetchPublic]);

  // Handle remove account - clear state if active account was removed
  const handleRemoveAccount = useCallback(async (sessionId: number) => {
    const success = await removeAccount(sessionId);
    if (success) {
      // Очищаем состояние — если удалили активный аккаунт,
      // refetchSession внутри removeAccount уже обновит isAuthorized.
      // Но нужно очистить локальный стейт (чаты, медиа, выбранный чат).
      setSelectedChat(null);
      clearMediaCache();
    }
    return success;
  }, [removeAccount]);

  // Handle add account - show auth flow
  const handleAddAccount = useCallback(() => {
    setShowAddAccount(true);
  }, []);

  // Handle cancel add account
  const handleCancelAddAccount = useCallback(() => {
    setShowAddAccount(false);
  }, []);

  // Callback после успешной авторизации — перезапрашиваем сессию в родителе
  const handleAuthComplete = useCallback(() => {
    refetchSession();
  }, [refetchSession]);

  // Callback после добавления аккаунта — закрываем форму и перезапрашиваем сессию
  const handleAddAccountComplete = useCallback(() => {
    setShowAddAccount(false);
    refetchSession();
  }, [refetchSession]);

  // Not authorized - show auth flow (without cancel button)
  if (!isAuthorized) {
    return (
      <FullHeightWrapper>
        <div className="h-full flex items-center justify-center p-8 bg-muted/30">
          <AuthFlow onComplete={handleAuthComplete} />
        </div>
      </FullHeightWrapper>
    );
  }

  // Adding new account - show auth flow with cancel button
  if (showAddAccount) {
    return (
      <FullHeightWrapper>
        <div className="h-full flex items-center justify-center p-8 bg-muted/30">
          <AuthFlow
            onComplete={handleAddAccountComplete}
            onCancel={handleCancelAddAccount}
          />
        </div>
      </FullHeightWrapper>
    );
  }

  // Текст typing индикатора для текущего чата
  const typingText = typingUsers.size > 0
    ? typingUsers.size === 1
      ? 'печатает...'
      : `${typingUsers.size} печатают...`
    : null;

  return (
    <FullHeightWrapper>
      {/* Connection state indicator */}
      {connectionState !== 'connected' && (
        <div className={`px-3 py-1.5 text-xs text-center font-medium ${
          connectionState === 'connecting'
            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
        }`}>
          {connectionState === 'connecting' ? 'Подключение...' : 'Ожидание сети...'}
        </div>
      )}
      <div className="flex h-full">
        {/* Chat list (left panel) */}
        <div className="w-[340px] min-w-[340px] max-w-[400px] h-full border-r">
          <ChatList
            chats={chats}
            isLoading={isChatsLoading}
            isLoadingMore={isChatsLoadingMore}
            hasMore={hasMoreChats}
            selectedChatId={selectedChat?.telegramChatId ?? null}
            onSelectChat={handleSelectChat}
            onLoadMore={fetchMoreChats}
            session={session}
            accounts={accounts}
            isAccountLoading={isAuthLoading}
            onSwitchAccount={handleSwitchAccount}
            onRemoveAccount={handleRemoveAccount}
            onAddAccount={handleAddAccount}
          />
        </div>

        {/* Chat view (center) */}
        <div className="flex-1 min-w-0 h-full">
          <ChatView
            chat={selectedChat}
            messages={messages}
            isLoading={isMessagesLoading}
            isLoadingMore={isMessagesLoadingMore}
            hasMore={hasMoreMessages}
            isSending={isSending}
            isUploadingFile={isUploadingFile}
            onSendMessage={sendMessage}
            onSendFile={sendFile}
            onSendSticker={handleSendSticker}
            onSendGif={handleSendGif}
            onEditMessage={editMessage}
            onDeleteMessage={deleteMessage}
            onForwardMessage={forwardMessage}
            onForwardMessages={forwardMessages}
            onMarkPublic={markAsPublic}
            onLinkToOrder={handleLinkToOrder}
            isMessageRead={isMessageRead}
            onLoadMore={fetchMoreMessages}
            typingText={typingText}
          />
        </div>

        {/* Public messages panel (right) */}
        <div className="w-[340px] min-w-[340px] max-w-[400px] h-full border-l">
          <PublicMessagesPanel
            messages={publicMessages}
            isLoading={isPublicLoading}
            onOpenChat={handleOpenChatFromPublic}
            onOpenOrder={handleOpenOrder}
          />
        </div>
      </div>

      {/* Link to Order Modal */}
      {linkToOrderChatId && (() => {
        const chatForModal = chats.find(c => c.id === linkToOrderChatId);
        return chatForModal ? (
          <LinkToOrderModal
            open={linkToOrderModalOpen}
            onClose={handleCloseLinkToOrderModal}
            chatId={linkToOrderChatId}
            chatTitle={chatForModal.title ?? 'Чат'}
            currentOrderId={chatForModal.linkedOrderId}
            onLink={linkChatToOrder}
            onUnlink={unlinkChatFromOrder}
          />
        ) : null;
      })()}
    </FullHeightWrapper>
  );
}

/**
 * Wrapper для полноэкранных страниц
 * Занимает всю доступную высоту (layout убирает padding для full-height страниц)
 */
function FullHeightWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="bg-background overflow-hidden h-full"
    >
      {children}
    </div>
  );
}
